/**
 * Image proxy utility to handle CORS/ORB issues with external images
 * Only 06se.com uses backend caching based on user setting
 * Other sites (misskon, 4khd) load directly
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Cache for 06se mode setting
let cachedMode = null;
let lastFetchTime = 0;
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Fetch 06se cache mode setting from backend
 * @returns {Promise<string>} - 'direct' or 'cache'
 */
const get06seCacheMode = async () => {
  const now = Date.now();
  
  // Return cached mode if still fresh
  if (cachedMode && (now - lastFetchTime) < CACHE_TTL) {
    return cachedMode;
  }
  
  try {
    const response = await axios.get(`${BACKEND_URL}/api/cache-settings`);
    cachedMode = response.data['06se_cache_mode'] || 'direct';
    lastFetchTime = now;
    return cachedMode;
  } catch (error) {
    console.error('Failed to fetch 06se cache mode:', error);
    return 'direct'; // Default to direct on error
  }
};

/**
 * Check if an image URL is from 06se.com (needs special handling)
 * @param {string} url - Image URL
 * @returns {boolean} - Whether URL is from 06se
 */
export const is06seImage = (url) => {
  if (!url) return false;
  return url.includes('wkphoto.cdn.bcebos.com') || url.includes('06se.com');
};

/**
 * Check if an image URL needs to be proxied through cache
 * ONLY 06se.com images need caching
 * @param {string} url - Image URL
 * @returns {boolean} - Whether the URL needs proxying
 */
export const needsProxy = (url) => {
  if (!url) return false;
  // Only cache 06se.com images due to CDN anti-hotlinking
  return is06seImage(url);
};

/**
 * Get cached/proxied image URL for 06se.com
 * @param {string} url - Original image URL
 * @param {string} source - Source website
 * @returns {string} - Cached URL
 */
export const getCachedImageUrl = (url, source = null) => {
  if (!url) return url;
  
  const params = new URLSearchParams({ url });
  if (source) {
    params.append('source', source);
  }
  return `${BACKEND_URL}/api/cached-image?${params.toString()}`;
};

/**
 * Test if image loads directly (without error)
 * @param {string} url - Image URL
 * @returns {Promise<boolean>} - True if loads successfully
 */
export const testImageLoad = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => {
      img.src = '';
      resolve(false);
    }, 3000); // 3 second timeout
    
    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    
    img.src = url;
  });
};

/**
 * Trigger backend to cache an image
 * @param {string} url - Image URL
 * @param {string} source - Source website
 * @returns {Promise<object>} - Cache trigger response
 */
export const triggerImageCache = async (url, source = null) => {
  try {
    const params = new URLSearchParams({ url });
    if (source) {
      params.append('source', source);
    }
    
    const response = await axios.post(`${BACKEND_URL}/api/trigger-cache?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to trigger cache:', error);
    return { status: 'error', cached: false };
  }
};

/**
 * Check cache status of an image
 * @param {string} url - Image URL
 * @returns {Promise<object>} - Cache status
 */
export const checkCacheStatus = async (url) => {
  try {
    const params = new URLSearchParams({ url });
    const response = await axios.get(`${BACKEND_URL}/api/cache-status?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to check cache status:', error);
    return { cached: false, ready: false };
  }
};

/**
 * Poll cache status until image is ready
 * @param {string} url - Image URL
 * @param {number} maxAttempts - Maximum polling attempts
 * @param {number} interval - Polling interval in ms
 * @returns {Promise<boolean>} - True if cached successfully
 */
export const pollCacheStatus = async (url, maxAttempts = 20, interval = 500) => {
  for (let i = 0; i < maxAttempts; i++) {
    const status = await checkCacheStatus(url);
    
    if (status.cached && status.ready) {
      return true;
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  
  return false; // Timeout
};

/**
 * Process image URL for display with smart fallback for 06se.com
 * Strategy:
 * 1. For non-06se images: Return original URL (direct load)
 * 2. For 06se images:
 *    a. Try loading directly first (in case CDN allows)
 *    b. If fails, trigger backend cache
 *    c. Poll until cached
 *    d. Return cached URL
 * 
 * @param {string} url - Image URL
 * @param {string} source - Source website (e.g., '06se', '4khd', 'misskon')
 * @returns {Promise<string>} - Processed image URL
 */
export const processImageUrl = async (url, source = null) => {
  if (!url) return url;
  
  // For non-06se images, return original URL (direct load)
  if (!is06seImage(url)) {
    return url;
  }
  
  // For 06se images, use cached endpoint directly
  // Backend will handle download if not cached
  return getCachedImageUrl(url, source || '06se');
};

/**
 * Legacy sync version - for backward compatibility
 * Returns URL based on 06se cache mode setting
 * @param {string} url - Image URL
 * @param {string} source - Source website
 * @returns {string} - Processed image URL
 * 
 * Note: This is sync so can't fetch setting. 
 * For 06se: Always route through cached-image endpoint.
 * Backend will handle redirect if mode is 'direct'.
 */
export const processImageUrlSync = (url, source = null) => {
  if (!url) return url;
  
  // Only process 06se images through cache endpoint
  // Backend will redirect to original URL if mode is 'direct'
  if (is06seImage(url)) {
    return getCachedImageUrl(url, source || '06se');
  }
  
  // Return original URL for other sources
  return url;
};

/**
 * Async version with setting check for 06se images
 * @param {string} url - Image URL  
 * @param {string} source - Source website
 * @returns {Promise<string>} - Processed image URL
 */
export const processImageUrlAsync = async (url, source = null) => {
  if (!url) return url;
  
  // For non-06se images, return original URL
  if (!is06seImage(url)) {
    return url;
  }
  
  // For 06se images, check user's cache mode preference
  const mode = await get06seCacheMode();
  
  if (mode === 'direct') {
    // Direct mode: return original URL (no caching)
    return url;
  } else {
    // Cache mode: route through cache endpoint
    return getCachedImageUrl(url, source || '06se');
  }
};
